import { styled } from '@linaria/react';
import AddIcon from '@mui/icons-material/Add';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CheckIcon from '@mui/icons-material/Check';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LayersIcon from '@mui/icons-material/Layers';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl';
import { useTranslation } from 'react-i18next';
import { BASEMAPS } from '../../../core/mapV2/indicators';
import { basemapQuery, fullscreenQuery, organizationBoundsQuery } from '../../../core/mapV2/queries';
import { basemapSet, fullscreenSet } from '../../../core/mapV2/state';
import theme from '../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../types';
import { DEFAULT_VIEW, MAX_ZOOM, MIN_ZOOM } from './map-styles';
import { SectionSearch } from './SectionSearch';

const Floating = styled.div`
    position: absolute;
    z-index: 1;
    border-radius: 8px;
    background-color: ${theme.color.white.main};
    box-shadow: ${theme.boxShadow.light};
`;

const SearchBox = styled(Floating)`
    top: 12px;
    left: 12px;
    width: 340px;
`;

const Column = styled.div`
    position: absolute;
    z-index: 1;
    top: 12px;
    right: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
`;

const Group = styled(Floating)`
    position: static;
    display: flex;
    flex-direction: column;
`;

// Controls laid over the map, as in the maquette: search at the top left; basemap, zoom, recenter and
// fullscreen at the top right. Rendered as a child of the Mapbox map so it can drive it with `useMap`.
export const MapControls: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { current: mapRef } = useMap();
    const basemap = useAppSelector(basemapQuery);
    const fullscreen = useAppSelector(fullscreenQuery);
    const bounds = useAppSelector(organizationBoundsQuery);
    const [zoom, setZoom] = useState<number>(DEFAULT_VIEW.zoom);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    // Keep the zoom buttons' disabled state in step with the map (they stop at the zoom bounds).
    useEffect(() => {
        const map = mapRef?.getMap();
        if (!map) {
            return undefined;
        }
        const update = () => setZoom(map.getZoom());
        update();
        map.on('zoom', update);

        return () => {
            map.off('zoom', update);
        };
    }, [mapRef]);

    const recenter = () => {
        if (bounds) {
            mapRef?.fitBounds(bounds, { padding: 100 });
        } else {
            mapRef?.flyTo({ center: [DEFAULT_VIEW.longitude, DEFAULT_VIEW.latitude], zoom: DEFAULT_VIEW.zoom });
        }
    };

    return (
        <>
            <SearchBox>
                <SectionSearch />
            </SearchBox>

            <Column data-testid="map-v2-map-controls">
                <Floating style={{ position: 'static' }}>
                    <Button
                        size="small"
                        startIcon={<LayersIcon fontSize="small" />}
                        onClick={(event) => setMenuAnchor(event.currentTarget)}
                        sx={{ textTransform: 'none', color: theme.color.grey.dark }}
                    >
                        {t('mapV2.map.basemap')}
                    </Button>
                </Floating>
                <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
                    {BASEMAPS.map((key) => (
                        <MenuItem
                            key={key}
                            selected={key === basemap}
                            sx={{ fontSize: 13, minWidth: 140 }}
                            onClick={() => {
                                dispatch(basemapSet(key));
                                setMenuAnchor(null);
                            }}
                        >
                            {t(`mapV2.map.basemaps.${key}`)}
                            {key === basemap ? (
                                <ListItemIcon sx={{ ml: 'auto', minWidth: 0 }}>
                                    <CheckIcon fontSize="small" />
                                </ListItemIcon>
                            ) : null}
                        </MenuItem>
                    ))}
                </Menu>

                <Group>
                    <IconButton
                        size="small"
                        aria-label={t('mapV2.map.zoomIn')}
                        title={t('mapV2.map.zoomIn')}
                        disabled={zoom >= MAX_ZOOM}
                        onClick={() => mapRef?.zoomIn()}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={t('mapV2.map.zoomOut')}
                        title={t('mapV2.map.zoomOut')}
                        disabled={zoom <= MIN_ZOOM}
                        onClick={() => mapRef?.zoomOut()}
                    >
                        <RemoveIcon fontSize="small" />
                    </IconButton>
                </Group>

                <Group>
                    <IconButton
                        size="small"
                        aria-label={t('mapV2.map.recenter')}
                        title={t('mapV2.map.recenter')}
                        onClick={recenter}
                    >
                        <CenterFocusStrongIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={t(fullscreen ? 'mapV2.map.exitFullscreen' : 'mapV2.map.fullscreen')}
                        title={t(fullscreen ? 'mapV2.map.exitFullscreen' : 'mapV2.map.fullscreen')}
                        onClick={() => dispatch(fullscreenSet(!fullscreen))}
                    >
                        {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                </Group>
            </Column>
        </>
    );
};
